import { FormFieldTypes, TextInputType, validationTypes } from "@repo/design-system/components/forms/libs";
import { DefaultActions } from "@repo/design-system/components/grids";
import PropTypes from 'prop-types';


// **************************   GetInputTypeByFieldType  ************************************ //
export const getInputTypeByFieldType = (fieldtype) => {
    switch (fieldtype) {
        case FormFieldTypes.PASSWORD:
            return TextInputType.PASSWORD;
        case FormFieldTypes.NUMERIC:
            return TextInputType.NUMBER;
        case FormFieldTypes.PHONE:
            return TextInputType.NUMBER;
        case FormFieldTypes.CHECKBOX:
            return TextInputType.CHECKBOX;
        case FormFieldTypes.EDITABLEDROPDOWN:
            return TextInputType.TEXT;
        case FormFieldTypes.DATEPICKER:
            return TextInputType.DATEPICKER;
        case FormFieldTypes.TEXTAREA:
            return TextInputType.TEXT;
        case FormFieldTypes.TEXTEDITOR:
            return TextInputType.TEXT;
        case FormFieldTypes.FILE:
            return TextInputType.FILE;
        case FormFieldTypes.RADIOBUTTON:
            return TextInputType.RADIO;
        default:
            return TextInputType.TEXT;
    }
};

// **************************   IsFieldPresent  ************************************ //
/**
 * Checks if a specific field exists within the form fields data by its field ID.
 * @param {Object} formFieldsData - The object containing all form fields data.
 * @param {string} fieldId - The identifier of the field to check.
 * @returns {boolean} - True if the field exists, otherwise false.
 */
export const isFieldPresent = (formFieldsData, fieldId) => {
    if (formFieldsData?.section) {
        for (const section of formFieldsData.section) {
            let field = section.fields.some((item) => item.dataField === fieldId);
            if (field) return true;

            if (section.rowGroup) {
                for (const row of section.rowGroup) {
                    field = row.fields.some((field) => field.id === fieldId);
                    if (field) return true;
                }
            }
        }
    }
    return false;
};

// **************************   GetFieldData  ************************************ //

/**
 * Finds a specific field data object within the form fields data by its field ID.
 * @param {Object} formFieldsData - The object containing all form fields data.
 * @param {string} fieldId - The identifier of the field to find.
 * @returns {Object|null} - The field data object if found, otherwise null.
 */
export const getFieldData = (formFieldsData, fieldId) => {
    if (formFieldsData?.section) {
        for (const section of formFieldsData?.section) {
            let field;
            if (section?.fields) {
                field = section.fields.find((item) => item.dataField === fieldId);
                if (field) return field;
            }
            if (section.rowGroup) {
                for (const row of section.rowGroup) {
                    field = row.fields.find((field) => field.id === fieldId);
                    if (field) return field;
                }
            }
        }
    }
    return null;
};
// Define propTypes for the function parameters
getFieldData.propTypes = {
    formFieldsData: PropTypes.array.isRequired,
    fieldId: PropTypes.string.isRequired
};


// **************************   SetDropDownOptionField  ************************************ //

/**
 * Sets the options for a dropdown field within form fields data based on API response data.
 * 
 * @param {Array} apiResponseData - The data received from an API response.
 * @param {string} valueField - The field name to be used for option values.
 * @param {string} labelField - The field name to be used for option labels.
 * @param {object} formFieldsData - The object containing form fields data.
 * @param {string} fieldId - The identifier for the dropdown field to be updated.
 * @param {function} [filterCondition=null] - An optional filter condition function to filter the API response data.
 * 
 * Example usage:
 * const filterCondition = (item) => {
        return (item) => item.isActive;
    };
 * setDropDownOptionField(apiResponseData, 'id', 'name', formFieldsData, 'countryDropdown', filterCondition);
 */
export const setDropDownOptionField = (apiResponseData, valueField, labelField, formFieldsData, fieldId, filterCondition = null, setFormData, extraKeys = []) => {

    // Filter the API response data if a filter condition is provided
    const filteredData = filterCondition ? apiResponseData?.filter(filterCondition) : apiResponseData;

    // Map the filtered data to the required format
    const mappedData = filteredData?.map((item) => {
        const options = {
            value: item[valueField],
            label: item[labelField],
        }

        // Dynamically add additional properties from extraKeys
        extraKeys?.forEach(key => {
            if (key in item) {
                options[key] = item[key];
            }
        });

        return options;
    });

    // Rebuild the section tree immutably (instead of mutating the field in place)
    // so shallow prop comparisons (React.memo on FormFieldsControl, etc.) pick up
    // the updated options and re-render immediately, rather than only on the next
    // unrelated form update — which was showing up as "dropdown is empty until you
    // open it a second time".
    if (formFieldsData?.section) {
        formFieldsData.section = formFieldsData.section.map((section) => {
            let matched = false;

            const fields = section.fields?.map((field) => {
                if (field.dataField !== fieldId) return field;
                matched = true;
                return { ...field, fieldSetting: { ...field.fieldSetting, options: mappedData } };
            });

            const rowGroup = section.rowGroup?.map((row) => {
                const rowFields = row.fields?.map((field) => {
                    if (field.id !== fieldId) return field;
                    matched = true;
                    return { ...field, fieldSetting: { ...field.fieldSetting, options: mappedData } };
                });
                return rowFields ? { ...row, fields: rowFields } : row;
            });

            return matched ? { ...section, fields, rowGroup } : section;
        });
    }
    setFormData && setFormData({ ...formFieldsData });
};
// Define propTypes for the function parameters
setDropDownOptionField.propTypes = {
    apiResponseData: PropTypes.array.isRequired,
    valueField: PropTypes.string.isRequired,
    labelField: PropTypes.string.isRequired,
    formFieldsData: PropTypes.array.isRequired,
    fieldId: PropTypes.string.isRequired,
    filterCondition: PropTypes.func
};


// **************************   SetGroupedDropDownOptionField  ************************************ //

/**
 * Builds grouped dropdown options from a flat parent-child API response and sets them on a form field.
 * Items without a parentIdField value become group headers; items with one become selectable options under their group.
 * Also automatically sets isGrouped: true on the field so DropDown flattens correctly for value resolution.
 *
 * @param {Array}    apiResponseData - Flat array of items from the API.
 * @param {string}   valueField      - Property name to use as each option's value (e.g. 'id').
 * @param {string}   labelField      - Property name to use as each option's label (e.g. 'name').
 * @param {string}   parentIdField   - Property name that links a child to its parent (e.g. 'parantId'). Items missing this are treated as group headers.
 * @param {object}   formFieldsData  - The form config object containing sections/rowGroups.
 * @param {string}   fieldId         - The id of the dropdown field to update.
 * @param {function} [setFormData]   - Optional state setter; called with a shallow copy to trigger re-render.
 * @param {Array}    [extraKeys=[]]  - Additional item properties to copy onto each option object.
 *
 * Example usage:
 * setGroupedDropDownOptionField(apiData, 'id', 'name', 'parantId', formConfig, 'skillId', setFormConfig);
 */
export const setGroupedDropDownOptionField = (
    apiResponseData,
    valueField,
    labelField,
    parentIdField,
    formFieldsData,
    fieldId,
    setFormData,
    extraKeys = []
) => {
    const data = apiResponseData || [];
    const categories = data.filter(item => !item[parentIdField]);
    const children = data.filter(item => !!item[parentIdField]);

    const groupedOptions = categories
        .map(cat => ({
            label: cat[labelField],
            options: children
                .filter(item => String(item[parentIdField]) === String(cat[valueField]))
                .map(item => {
                    const option = {
                        value: String(item[valueField]),
                        label: item[labelField],
                    };
                    extraKeys.forEach(key => {
                        if (key in item) option[key] = item[key];
                    });
                    return option;
                })
        }))
        .filter(group => group.options.length > 0);

    const dropdownField = getFieldData(formFieldsData, fieldId);
    if (dropdownField) {
        dropdownField.fieldSetting.options = groupedOptions;
        dropdownField.fieldSetting.isGrouped = true;
    }
    setFormData && setFormData({ ...formFieldsData });
};

setGroupedDropDownOptionField.propTypes = {
    apiResponseData: PropTypes.array.isRequired,
    valueField: PropTypes.string.isRequired,
    labelField: PropTypes.string.isRequired,
    parentIdField: PropTypes.string.isRequired,
    formFieldsData: PropTypes.object.isRequired,
    fieldId: PropTypes.string.isRequired,
    setFormData: PropTypes.func,
    extraKeys: PropTypes.arrayOf(PropTypes.string)
};


// **************************   SetFieldSetting  ************************************ //
/**
 * Sets a specific field setting for a given field within form fields data.
 * 
 * @param {object} formFieldsData - The object containing form fields data.
 * @param {string} fieldId - The identifier for the field to be updated.
 * @param {string} fieldSettingType - The type of setting to be updated (e.g., 'isMultiSelect', 'isDisabled') and Also, create Enums for the setting type.
 * @param {any} [value=false] - The value to set for the specified setting type. Default is `false`.
 * 
 * Example usage:
 * setFieldSetting(formFieldsData, 'countryDropdown', FieldSettingType.DISABLED/FieldSettingType.MULTISELECT, true);
 */
export const setFieldSetting = (formFieldsData, fieldId, fieldSettingType, value = false) => {
    const selectField = getFieldData(formFieldsData, fieldId);
    if (selectField && selectField.fieldSetting) {
        switch (fieldSettingType) {
            case 'isMultiSelect':
                selectField.fieldSetting.isMultiSelect = value;
                break;
            case 'isDisable':
                selectField.fieldSetting.isDisable = value;
                break;
            case 'isPrimaryButtonVisible':
                selectField.inputButtonGroupConfig.isPrimaryButtonVisible = value;
                break;
            case 'isInfoButtonVisible':
                selectField.fieldSetting.isFormControl = value;
                break;
            case 'isText':
                //selectField.fieldSetting.isText = value;
                selectField.fieldSetting.isText = value;
                break;
            case 'CKEditorDisabled':
                selectField.fieldSetting.isDisable = value;
                break;
            case 'isMultiple':
                selectField.fieldSetting.isMultiple = value;
                break;
            case 'isFileNameCleared':
                selectField.fieldSetting.isFileNameCleared = value;
                break;
            case 'isTemplateBtnDisable':
                selectField.fieldSetting.isTemplateBtnDisable = value;
                break;
            case 'isLabelIconVisible':
                selectField.fieldSetting.labelConfig.isLabelIconVisible = value;
                break;
            case 'isLoading':
                selectField.fieldSetting.isLoading = value;
                break;
            case 'isMasked':
                selectField.fieldSetting.isMasked = value;
                break;
            default:
                break;
        }
    }
};
// Define propTypes for the function parameters
setFieldSetting.propTypes = {
    formFieldsData: PropTypes.array.isRequired,
    fieldId: PropTypes.string.isRequired,
    fieldSettingType: PropTypes.array.isRequired,
    value: PropTypes.bool
};


// **************************   UpdateFieldValidationInForm  ************************************ //
/**
 * Add or remove validation from specific form fields in the form data.
 * 
 * @param {object} formData - The current form data object.
 * @param {Array} fieldsToUpdate - An array of field dataField IDs to update validation for.
 * @param {boolean} isRemove - Determines whether to remove the validation (true) or add required validation (false).
 * @returns {object} - The modified form data with updated validation.
 */
export const updateFieldValidationInForm = (formData, fieldToUpdate, isRemove = false) => {
    // Create a copy of the formData object
    const modifiedData = { ...formData };

    // Normalize fieldToUpdate to lowercase for case-insensitive comparison
    const lowerCaseFieldsToUpdate = Array.isArray(fieldToUpdate) ?
        fieldToUpdate.map((field) => field?.toLowerCase()) :
        [fieldToUpdate?.toLowerCase()];

    // Helper function to update validation
    const updateValidation = (field) => {
        if (isRemove) {
            const { validation, ...rest } = field;
            return rest;  // Remove validation
        } else {
            return { ...field, validation: [{ type: validationTypes.REQUIRE }] };  // Add required validation
        }
    };

    // If there are sections, iterate over each to update validation in fields and nested fields
    if (modifiedData.section) {
        modifiedData.section = modifiedData.section.map((section) => {
            const updatedSection = { ...section };

            // Update validation for fields at the section level
            updatedSection.fields = updatedSection.fields?.map((field) =>
                lowerCaseFieldsToUpdate.includes(field.dataField?.toLowerCase())
                    ? updateValidation(field)
                    : field
            );

            // If there are rowGroups, update validation for fields within each row
            if (updatedSection.rowGroup) {
                updatedSection.rowGroup = updatedSection.rowGroup.map((row) => {
                    const updatedRow = { ...row };
                    updatedRow.fields = updatedRow.fields.map((field) =>
                        lowerCaseFieldsToUpdate.includes(field.dataField?.toLowerCase())
                            ? updateValidation(field)
                            : field
                    );
                    return updatedRow;
                });
            }

            return updatedSection;
        });
    }

    // Return the modified form data
    return modifiedData;
};
// Define propTypes for the function parameters
updateFieldValidationInForm.propTypes = {
    formData: PropTypes.object.isRequired,         // Form data object containing the sections and fields
    fieldsToUpdate: PropTypes.array.isRequired,    // Array of field IDs whose validation needs to be updated
    isRemove: PropTypes.bool.isRequired            // Boolean flag to determine if validation should be removed or added
};


// **************************   GetDropdownLabelName  ************************************ //
export const getDropdownLabelName = (apiResponseData, valueField, labelField, selectedId) => {
    if (apiResponseData && selectedId && valueField && labelField) {
        // Map the data to the required format
        const mappedData = apiResponseData.map((item) => ({
            value: item[valueField],
            label: item[labelField]
        }));

        // Find the matching entry based on selectedId
        const labelName = mappedData.find(data => Number(data.value) === Number(selectedId));
        if (labelName) {
            return labelName.label; // Return the label field value
        }
    }
    return null;
};

/**
 * Remove specific form fields from form data and update the state with the modified data.
 * 
 * @param {object} formData - The current form data object.
 * @param {Array} fieldsToRemove - An array of field dataField IDs to remove from the form data.
 */
export const removeFormFields = (formData, fieldsToRemove) => {
    // Create a copy of the formData object
    const modifiedData = { ...formData };

    // If formFields are directly in formData, filter them
    if (modifiedData.formFields) {
        modifiedData.formFields = modifiedData.formFields.filter((field) => !fieldsToRemove.includes(field.dataField));
    }

    // Normalize fieldsToRemove to lowercase for case-insensitive comparison
    const lowerCaseFieldsToRemove = fieldsToRemove.map((field) => field?.toLowerCase());

    // If there are sections, iterate over each to filter fields and nested fields
    if (modifiedData.section) {
        modifiedData.section = modifiedData.section.map((section) => {
            // Remove fields at the section level
            const updatedSection = { ...section };
            if (updatedSection.fields) {
                updatedSection.fields = updatedSection.fields.filter((field) => !lowerCaseFieldsToRemove.includes(field.dataField?.toLowerCase()));
            }

            // If there are rowGroups, filter fields within each row
            if (updatedSection.rowGroup) {
                updatedSection.rowGroup = updatedSection.rowGroup.map((row) => {
                    const updatedRow = { ...row };
                    updatedRow.fields = updatedRow.fields.filter(
                        (field) => !fieldsToRemove.includes(field.id)
                    );
                    return updatedRow;
                });
            }

            return updatedSection;
        });
    }
    // Return the modified form data
    return modifiedData;

};
// Define propTypes for the function parameters
removeFormFields.propTypes = {
    formData: PropTypes.object.isRequired,
    fieldsToRemove: PropTypes.array.isRequired
};


/**
 * Resets form data and optionally other states to their initial values.
 * 
 * @param {object} formData - The current form data object.
 * @param {function} setFormData - The state setter function for the form data.
 * @param {object} initialState - The initial state object to reset the form data to.
 * @param {Array} [otherSettersWithValues=[]] - An optional array of objects containing setter functions and values to reset other states to.
 * 
 * Example object for 
    otherSettersWithValues: [
        { setter: setSomeState, value: null },
        { setter: setAnotherState, value: 0 }
    ]
*/
export const onResetForm = (formData, setFormData, initialState = null, otherSettersWithValues = []) => {
    // Create a copy of the form data and reset it to its initial state
    let resetData = { ...formData };
    const initialData = !initialState ? resetData.initialState : initialState;
    resetData.initialState = { ...initialData };
    setFormData(resetData);

    // Reset other states with specified values if provided
    if (otherSettersWithValues.length > 0) {
        otherSettersWithValues.forEach(({ setter, value }) => setter(value));
    }
};

onResetForm.propTypes = {
    formData: PropTypes.object.isRequired,
    setFormData: PropTypes.func.isRequired,
    initialState: PropTypes.object,
    otherSettersWithValues: PropTypes.arrayOf(PropTypes.shape({
        setter: PropTypes.func.isRequired,
        value: PropTypes.any.isRequired, // Use PropTypes.any because value can be of any type
    })),
};



export const getValue = (data) => {
    return data && typeof data === "object" ? data.value : data;
};


// **************************   CreateLazyFetchOptions  ************************************ //

/**
 * Creates a fetchOptions callback for LAZY_SELECT fields from an RTK Query lazy trigger.
 * Eliminates the need to write individual useCallback wrappers per dropdown.
 *
 * Supports two modes:
 *   1. Normal paginated fetch — { page, search }
 *   2. Single-item resolve   — { resolveValue: id } (used internally by
 *      FormLazyDropdownControls to prefill a selected value by ID)
 *
 * @param {Function} apiQuery           - RTK Query lazy trigger (e.g. getInstitutes from useLazy…Query)
 * @param {Object}   [config={}]
 * @param {string}   [config.valueField='id']   - Item property for option value
 * @param {string}   [config.labelField='name'] - Item property for option label
 * @param {number}   [config.size=10]            - Page size for paginated requests
 * @param {boolean}  [config.spreadItem=false]   - Spread all item properties into option object
 * @param {string[]} [config.extraKeys=[]]       - Additional item properties to copy into option
 * @param {Function} [config.resolveApiQuery]    - Optional separate API for single-item resolve by id
 *
 * @returns {Function} fetchOptions — async ({ page, search, resolveValue, dependentValues }) => { items, hasMore }
 *
 * Example:
 *   const fetchInstitutes = useMemo(
 *       () => createLazyFetchOptions(getInstitutes, {
 *           labelField: 'name',
 *           spreadItem: true,
 *       }),
 *       [getInstitutes],
 *   );
 *   setLazyDropdownFetchOptions(cfg, 'admittedInstituteId', fetchInstitutes);
 */
export const createLazyFetchOptions = (apiQuery, config = {}) => {
    const {
        valueField = 'id',
        labelField = 'name',
        size = 10,
        spreadItem = false,
        extraKeys = [],
        resolveApiQuery,
    } = config;

    const mapItem = (item) => {
        if (!item) return null;
        const option = { value: item[valueField], label: item[labelField] };
        if (spreadItem) Object.assign(option, item);
        extraKeys.forEach(k => { if (k in item) option[k] = item[k]; });
        return option;
    };

    return async ({ page, search, resolveValue, dependentValues } = {}) => {
        // Resolve any { value, label } dependency values (e.g. a university SELECT
        // field) down to their raw IDs so they can be sent as query params.
        const resolvedDependentValues = dependentValues
            ? Object.fromEntries(
                Object.entries(dependentValues).map(([key, val]) => [key, getValue(val)])
            )
            : {};

        // ── Resolve single item by ID (for prefill) ─────────────────────
        if (resolveValue !== undefined && resolveValue !== null) {
            if (resolveApiQuery) {
                try {
                    const queryResult = resolveApiQuery(resolveValue);
                    if (queryResult && typeof queryResult.unwrap === 'function') {
                        const res = await queryResult.unwrap();
                        const item = res?.data;
                        return { items: item ? [mapItem(item)] : [], hasMore: false };
                    } else if (queryResult && typeof queryResult === 'object') {
                        const res = await apiQuery(queryResult).unwrap();
                        const match = (res?.data?.content || []).find(
                            i => String(i[valueField]) === String(resolveValue) ||
                                String(i[labelField]) === String(resolveValue)
                        );
                        return { items: match ? [mapItem(match)] : [], hasMore: false };
                    }
                } catch (_) {
                    return { items: [], hasMore: false };
                }
            }
            // Fallback: use the paginated API and filter by ID or label in the response
            try {
                const res = await apiQuery({ page: 0, size, search: resolveValue, ...resolvedDependentValues }).unwrap();
                const match = (res?.data?.content || []).find(
                    i => String(i[valueField]) === String(resolveValue) ||
                        String(i[labelField]) === String(resolveValue)
                );
                return { items: match ? [mapItem(match)] : [], hasMore: false };
            } catch (_) {
                return { items: [], hasMore: false };
            }
        }

        // ── Normal paginated fetch ──────────────────────────────────────
        const res = await apiQuery({ page, size, search, ...resolvedDependentValues }).unwrap();
        return {
            items: (res?.data?.content || []).map(mapItem).filter(Boolean),
            hasMore: !res?.data?.last,
        };
    };
};


// **************************   SetLazyDropdownFetchOptions  ************************************ //

/**
 * Wires a `fetchOptions` callback into a LAZY_SELECT field's fieldSetting.
 *
 * Use this the same way you use setDropDownOptionField for regular dropdowns —
 * call it inside a useMemo (or useEffect) in the parent component.
 *
 * @param {object}   formFieldsData  - The form config object containing sections/rowGroups.
 * @param {string}   fieldId         - The dataField id of the LAZY_SELECT field to update.
 * @param {Function} fetchOptions    - Async fn: ({ page, search, resolveValue }) => { items, hasMore }.
 *                                    Use createLazyFetchOptions to generate this, or write manually.
 *                                    Must be wrapped in useCallback/useMemo in the parent to keep
 *                                    the reference stable — the lazy dropdown component resets and
 *                                    re-fetches whenever this reference changes.
 * @param {Function} [fetchOptionById] - Optional async fn: (id) => optionObject | null.
 *                                    Used by FormLazyDropdownControls for prefill resolution
 *                                    when a simpler single-item API exists. If not provided,
 *                                    the component falls back to fetchOptions({ resolveValue: id }).
 *
 * Example (with factory):
 *   const fetchInstitutes = useMemo(
 *       () => createLazyFetchOptions(getInstitutes, { labelField: 'name', spreadItem: true }),
 *       [getInstitutes],
 *   );
 *   setLazyDropdownFetchOptions(config, 'admittedInstituteId', fetchInstitutes);
 *
 * Example (manual):
 *   const fetchUniversities = useCallback(async ({ page, search }) => {
 *       const res = await getUniversities({ page, size: 10, search }).unwrap();
 *       return {
 *           items  : res.data.content.map(u => ({ value: u.id, label: u.value })),
 *           hasMore: !res.data.last,
 *       };
 *   }, [getUniversities]);
 *   setLazyDropdownFetchOptions(config, 'universityName', fetchUniversities);
 */
export const setLazyDropdownFetchOptions = (formFieldsData, fieldId, fetchOptions, fetchOptionById) => {
    const field = getFieldData(formFieldsData, fieldId);
    if (field && field.fieldSetting) {
        field.fieldSetting.fetchOptions = fetchOptions;
        if (fetchOptionById) {
            field.fieldSetting.fetchOptionById = fetchOptionById;
        }
    }
};


// **************************   FilterGridActionsByStatus  ************************************ //

/**
 * Filters default and custom grid actions based on matching status values or IDs.
 *
 * @param {Object} rowData - The row data object for the current grid row.
 * @param {Object} col - The action column definition object (containing defaultActions, customActions).
 * @param {Array<string|number>} targetStatuses - Array of status values or status IDs that trigger hiding actions.
 * @param {Array<string>} [hiddenActionKeys=['EDIT', 'DELETE']] - Array of action keys to hide when status matches.
 * @param {string} [statusField='statusId'] - Primary status field name in rowData to evaluate.
 * @param {Array} [defaultActionsList=DefaultActions] - Default actions array fallback.
 * @returns {Array} Filtered list of action objects to display.
 */
export const filterGridActionsByStatus = (rowData, col, targetStatuses = [],
    hiddenActionKeys = ["EDIT", "DELETE"], statusField = "statusId",
    defaultActionsList = DefaultActions) => {

    if (!col) return [];

    const rawStatusId = rowData?.[statusField];
    const rawStatusName = rowData?.status;

    const isMatch = targetStatuses.some((target) => {
        if (rawStatusId !== undefined && rawStatusId !== null &&
            String(rawStatusId) === String(target)) {
            return true;
        }
        if (rawStatusName !== undefined && rawStatusName !== null &&
            String(rawStatusName).toUpperCase() === String(target).toUpperCase()) {
            return true;
        }
        return false;
    });

    const availableActions = [
        ...(defaultActionsList || DefaultActions).filter((action) => col.defaultActions?.[action.key]),
        ...(col.customActions || [])
    ];

    if (isMatch) {
        return availableActions.filter((action) => !hiddenActionKeys.includes(action.key));
    }

    return availableActions;
};

filterGridActionsByStatus.propTypes = {
    rowData: PropTypes.object,
    col: PropTypes.object.isRequired,
    targetStatuses: PropTypes.array.isRequired,
    hiddenActionKeys: PropTypes.arrayOf(PropTypes.string),
    statusField: PropTypes.string,
    defaultActionsList: PropTypes.array
};


